import Intercom, {
  hide,
  show,
  shutdown,
  update,
} from "@intercom/messenger-js-sdk";
import { useI18n } from "providers/I18nProvider";
import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID as string;

type IntercomValue = string | number | null | undefined | boolean;

export type IntercomContextType = {
  boot: () => void;
  show: () => void;
  shutdown: () => void;
  hide: () => void;
  update: (data: {
    [key: string]: IntercomValue | Record<string, IntercomValue>;
  }) => void;
};

export const IntercomContext = createContext<IntercomContextType>({
  boot: () => {},
  show: () => {},
  shutdown: () => {},
  hide: () => {},
  update: () => {},
});

type Props = {
  children: ReactNode;
};

export const IntercomProvider = ({ children }: Props) => {
  const { language } = useI18n();

  const bootMessenger = useCallback(() => {
    if (!INTERCOM_APP_ID) return;

    Intercom({
      app_id: INTERCOM_APP_ID,
      region: "eu",
      language_override: language,
      z_index: 50,
      hide_default_launcher: true,
    });
  }, [language]);

  useEffect(() => {
    bootMessenger();
  }, [bootMessenger]);

  const contextValue = useMemo<IntercomContextType>(() => {
    return { boot: bootMessenger, show, shutdown, hide, update };
  }, [bootMessenger]);

  return (
    <IntercomContext.Provider value={contextValue}>
      {children}
    </IntercomContext.Provider>
  );
};

export const useIntercom = (): IntercomContextType => {
  const context = useContext(IntercomContext);
  if (!context) {
    throw new Error("useIntercom must be used within an IntercomProvider");
  }
  return context;
};
